import cn from 'classnames'
import Image from 'next/image'
import {FC, useLayoutEffect, useRef, useState} from 'react'

import {Link, useTranslation} from '@/i18n'
import {ICON_SIZE_MEDIUM} from '@/theme/icons'
import {FORM_CONTAINER_PADDING_MOBILE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'
import {AnalyticsTrackedEvent} from '@/utils/tracking/types'

import {Button} from '../content/Button'
import {ButtonSize} from '../content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '../content/Text'
import {ArrowIcon} from '../icons/ArrowIcon'
import {CheckCircleIcon} from '../icons/CheckCircleIcon'
import {TimeIcon} from '../icons/TimeIcon'

export enum SuccessPageTimelineStepStatus {
  current = 'current',
  completed = 'completed',
  disabled = 'disabled',
}

export type SuccessPageNextStepsStep = {
  imageSrc: string
  imageAlt: string
  title: string
  description?: string
  linkHref?: string
  linkText?: string
  trackedEvent?: AnalyticsTrackedEvent
  status: SuccessPageTimelineStepStatus
}

type SuccessPageNextStepsProps = {
  title: string
  subtitle: string
  steps: SuccessPageNextStepsStep[]
}

export const SuccessPageNextSteps: FC<React.PropsWithChildren<SuccessPageNextStepsProps>> = ({
  title,
  subtitle,
  steps,
}) => {
  const {t} = useTranslation()
  const headerRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState('0px')

  useLayoutEffect(() => {
    setHeaderHeight(`${headerRef.current?.clientHeight}px`)
  }, [headerRef])

  return (
    <>
      <div className="wrapper">
        <div className="header" ref={headerRef}>
          <Text align="center" variant={TextVariant.h4}>
            {title}
          </Text>
          <div className="subtitle">
            <Text align="center" color={theme.palette.text.light}>
              {subtitle}
            </Text>
          </div>
        </div>
        <div className="timeline-wrapper">
          <ul className="timeline">
            {steps.map(
              ({
                imageSrc,
                imageAlt,
                title,
                description,
                linkHref,
                linkText,
                trackedEvent,
                status,
              }) => {
                const isCurrent = status === SuccessPageTimelineStepStatus.current
                const isCompleted = status === SuccessPageTimelineStepStatus.completed

                return (
                  <li
                    key={title}
                    className={cn('item', isCurrent && 'current', isCompleted && 'completed')}
                  >
                    <div className="image">
                      <Image
                        src={imageSrc}
                        alt={imageAlt}
                        width={64}
                        height={64}
                        style={{objectFit: 'contain'}}
                      />
                    </div>
                    <div className="icon">
                      {isCurrent ? (
                        <TimeIcon />
                      ) : isCompleted ? (
                        <CheckCircleIcon backgroundColor={theme.palette.success.main} />
                      ) : (
                        <span className="dot" />
                      )}
                    </div>
                    <div className="content">
                      <Text
                        align="center"
                        fontWeight={TextFontWeight.bold}
                        color={
                          isCompleted
                            ? theme.palette.success.main
                            : isCurrent
                            ? theme.palette.text.default
                            : theme.palette.gray[400]
                        }
                      >
                        {t(title)}
                      </Text>
                      {description && (
                        <div className="description">
                          <Text
                            variant={TextVariant.text14}
                            align="center"
                            color={
                              isCompleted || isCurrent
                                ? theme.palette.text.light
                                : theme.palette.gray[400]
                            }
                          >
                            {t(description)}
                          </Text>
                        </div>
                      )}
                      {linkHref && linkText && (
                        <div className="button-wrapper">
                          <Link
                            href={linkHref}
                            onClick={() => {
                              if (trackedEvent) {
                                analytics.track(trackedEvent.name, trackedEvent.parameters)
                              }
                            }}
                          >
                            <Button
                              type="button"
                              size={ButtonSize.small}
                              icon={<ArrowIcon />}
                              isHoverable
                            >
                              {t(linkText)}
                            </Button>
                          </Link>
                        </div>
                      )}
                    </div>
                  </li>
                )
              },
            )}
          </ul>
        </div>
      </div>

      <style jsx>{`
        .wrapper {
          background-color: ${theme.palette.background.butter};
          padding: ${theme.spacing[6]} ${theme.spacing[8]};
          margin-top: ${theme.spacing[7]};
          border-radius: 16px;
        }
        .header {
          position: relative;
          z-index: 1;
        }
        .subtitle {
          margin-top: ${theme.spacing[1]};
        }
        .timeline-wrapper {
          margin: ${theme.spacing[6]} auto 0;
        }
        .timeline {
          display: flex;
          justify-content: center;
        }
        .item {
          flex: 1;
          position: relative;
        }
        .content {
          max-width: 230px;
          margin: 0 auto;
        }
        .image {
          display: flex;
          justify-content: center;
          margin: 0 auto ${theme.spacing[3.5]};
        }
        .icon {
          display: flex;
          justify-content: center;
          margin: 0 auto ${theme.spacing[2]};
        }
        .dot {
          width: 12px;
          height: 12px;
          flex: 0 0 12px;
          background-color: ${theme.palette.gray[300]};
          border-radius: 50%;
          margin: ${theme.spacing[1]};
        }
        .icon::before,
        .icon::after {
          content: '';
          position: relative;
          top: calc(${ICON_SIZE_MEDIUM} / 2);
          width: 100%;
          height: 2px;
          border-radius: 4px;
          background-color: ${theme.palette.gray[300]};
        }
        .icon::before {
          margin-right: ${theme.spacing[2]};
        }
        .icon::after {
          margin-left: ${theme.spacing[2]};
        }
        .item.completed .icon::after,
        .item.current .icon::before {
          background-color: ${theme.palette.success.main};
        }
        .item.completed:first-child .icon::before {
          background: linear-gradient(
            90deg,
            rgba(62, 137, 91, 0) 0%,
            ${theme.palette.success.main} 40%
          );
        }
        .item:last-child .icon::after {
          background: linear-gradient(
            90deg,
            ${theme.palette.gray[300]} 60%,
            rgba(208, 213, 221, 0) 100%
          );
        }
        .description {
          margin-top: ${theme.spacing[2]};
        }
        .button-wrapper {
          text-align: center;
          margin-top: ${theme.spacing[2.5]};
        }
        @media ${TABLET_SCREENSIZE} {
          .wrapper {
            padding: ${theme.spacing[6]} ${theme.spacing[3]};
          }
          .content {
            max-width: 200px;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .wrapper {
            padding: ${theme.spacing[5]} 0 0;
            margin-top: ${theme.spacing[5.5]};
          }
          .subtitle {
            max-width: 270px;
            margin: ${theme.spacing[1]} auto 0;
          }
          .timeline-wrapper {
            margin-top: calc(-${headerHeight} - ${theme.spacing[5]});
            height: calc(240px + ${headerHeight} + (${theme.spacing[5]} * 3));
            overflow: hidden;
            margin-left: -${FORM_CONTAINER_PADDING_MOBILE};
            margin-right: -${FORM_CONTAINER_PADDING_MOBILE};
          }
          .timeline {
            width: 100%;
            padding-left: ${theme.spacing[4]};
            padding-right: ${theme.spacing[4]};
            overflow-x: auto;
            overflow-y: hidden;
            justify-content: flex-start;
          }
          .item {
            max-width: inherit;
            min-width: 240px;
            background-color: ${theme.palette.background.butter};
            padding: calc(${headerHeight} + (${theme.spacing[5]} * 2)) 0 ${theme.spacing[5]};
          }
          .item:first-child {
            border-top-left-radius: 16px;
            border-bottom-left-radius: 16px;
          }
          .item:last-child {
            border-top-right-radius: 16px;
            border-bottom-right-radius: 16px;
          }
        }
      `}</style>
    </>
  )
}
